import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';

import type { WebWidgetModel } from '@web/types';

import { TranslateModule } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { map, Subject, take, type Observable } from 'rxjs';

import { AppFacade } from '@web/store/app/app.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { ToolbarWidgetComponent } from '@web/shared/components/web-toolbar-widget/web-toolbar-widget.component';
import { WebWidgetGroupPipe } from '@web/shared/pipes/widget-group.pipe';

@Component({
    selector: 'web-add-widget',
    templateUrl: './add-widget.component.html',
    styleUrls: ['./add-widget.component.less'],
    imports: [AsyncPipe, TranslateModule, ToolbarWidgetComponent, WebWidgetGroupPipe],
})
export class AddWidgetComponent {
    @Input() public widgetType: string = 'default';
    @Input() public isDisabledLogoWidget: boolean;
    @Output() public addWidget: EventEmitter<WebWidgetModel> = new EventEmitter<WebWidgetModel>();

    private readonly bsModalRef: BsModalRef = inject(BsModalRef);
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);

    public readonly isAdmin$: Observable<boolean> = this.appFacade.isAdmin$;
    public readonly isEduSite$: Observable<boolean> = this.pagesFacade.isEduSite$;
    public readonly eduUserTariff$: Observable<string> = this.userFacade.eduUserTariff$;

    public onClose: Subject<WebWidgetModel> = new Subject<WebWidgetModel>();

    public isWidgetDisabled(widget: WebWidgetModel) {
        return this.isAdmin$.pipe(
            take(1),
            map((isAdmin) => {
                return !isAdmin && (widget.params.disabled || !widget.permission.isReleased);
            })
        );
    }

    public onAddWidget(widget: WebWidgetModel): void {
        this.isWidgetDisabled(widget).subscribe((disabled) => {
            if (disabled) {
                return;
            }

            this.onClose.next(widget);
            this.bsModalRef.hide();
        });
    }

    public get widgets$(): Observable<WebWidgetModel[]> {
        if (this.isSpecific) {
            return this.widgetsService.getSpecificWidgets$();
        }

        if (this.isZero) {
            return this.widgetsService.getZeroWidgets$();
        }

        return this.widgetsService.getVisibleWidgets$();
    }

    public get isSpecific(): boolean {
        return this.widgetType === 'specific';
    }

    public get isZero(): boolean {
        return this.widgetType === 'zero';
    }

    public get isDefault(): boolean {
        return this.widgetType === 'default';
    }

    public onCancel(): void {
        this.onClose.next(null);
        this.bsModalRef.hide();
    }
}
